'use client';

import { useReveal } from '@/hooks/use-reveal';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Send, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export default function Contact() {
  const { ref, visible } = useReveal();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: '', // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [autoResponse, setAutoResponse] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Échec de l'envoi");
      }

      toast.success('Message envoyé avec succès.', {
        description: 'Notre équipe vous répondra sous 24h.',
      });
      setSubmitted(true);
      if (data.autoResponse) setAutoResponse(data.autoResponse);
      setForm({ name: '', email: '', phone: '', message: '', website: '' });
    } catch (err: any) {
      toast.error(err.message || "Une erreur est survenue.", {
        description: 'Veuillez réessayer ou nous téléphoner.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-28 md:py-40 bg-[#2B1810] overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/paris-boutique.png"
          alt="Boutique ASMAO STYLE CUIR Rue Clignancourt Paris"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B1810] via-[#2B1810]/95 to-[#1A0E08]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Left — Info */}
          <div>
            <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-px bg-[#C9A961]" />
                <span className="text-[#C9A961] font-serif-display text-sm tracking-[0.4em] uppercase">
                  Nous Trouver
                </span>
              </div>
            </div>

            <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-[#F8F1E4] text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] mb-8`}>
              Venez vivre
              <br />
              <span className="italic text-luxe-gold">l'expérience</span>
            </h2>

            <p className={`reveal ${visible ? 'visible' : ''} text-[#F8F1E4]/80 text-lg font-serif-display font-light leading-relaxed mb-10`}>
              Notre boutique-atelier vous accueille au cœur du 18e arrondissement de Paris.
              Sur rendez-vous, nous vous offrons un moment privilégié pour découvrir nos
              collections, choisir votre cuir et concevoir une pièce qui vous ressemble.
            </p>

            {/* Contact details */}
            <div className="space-y-6">
              {[
                {
                  icon: MapPin,
                  label: 'Adresse',
                  value: 'Rue Clignancourt, 75018 Paris',
                  sub: 'Métro : Anvers (L2) · Barbès — Rochechouart (L2/L4)',
                },
                {
                  icon: Clock,
                  label: 'Horaires',
                  value: 'Du mardi au samedi · 10h — 19h',
                  sub: 'Sur rendez-vous conseillé',
                },
                {
                  icon: Phone,
                  label: 'Téléphone',
                  value: '+33 6 99 70 39 84',
                  sub: 'Du lundi au samedi, 10h — 18h',
                },
                {
                  icon: Mail,
                  label: 'Email',
                  value: 'asmao@edisem.fr',
                  sub: 'Réponse sous 24h ouvrées',
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`reveal ${visible ? 'visible' : ''} flex gap-5 group`}
                    style={{ transitionDelay: `${idx * 80}ms` }}
                  >
                    <div className="shrink-0 w-12 h-12 rounded-full border border-[#C9A961]/40 flex items-center justify-center group-hover:border-[#C9A961] group-hover:bg-[#C9A961]/10 transition-all duration-500">
                      <Icon className="text-[#C9A961]" size={18} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display mb-1">
                        {item.label}
                      </div>
                      <div className="text-[#F8F1E4] text-base font-medium">{item.value}</div>
                      <div className="text-[#F8F1E4]/55 text-sm font-serif-display italic mt-0.5">
                        {item.sub}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social */}
            <div className={`reveal ${visible ? 'visible' : ''} mt-10 flex items-center gap-4`}>
              <span className="text-[#F8F1E4]/60 text-xs tracking-[0.3em] uppercase font-serif-display">
                Suivez-nous
              </span>
              <div className="flex gap-3">
                {[Instagram, Facebook].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#C9A961]/30 flex items-center justify-center text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300"
                    aria-label="Réseau social"
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Form / Success */}
          <div className={`reveal ${visible ? 'visible' : ''} relative`}>
            <div className="bg-[#F8F1E4]/95 backdrop-blur-md p-8 md:p-12 rounded-sm shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
              {submitted ? (
                <div className="text-center py-8 animate-fade-up">
                  <div className="w-20 h-20 rounded-full border-2 border-[#8B4513] bg-[#8B4513] flex items-center justify-center mx-auto mb-6">
                    <Check className="text-[#F8F1E4]" size={36} strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-3xl text-[#2B1810] mb-3">
                    Message bien reçu
                  </h3>
                  <p className="text-[#5C2E03]/80 font-serif-display leading-relaxed mb-6">
                    Merci pour votre confiance. Notre équipe vous répondra sous 24 heures
                    au numéro ou à l'email indiqué.
                  </p>
                  {autoResponse && (
                    <div className="mt-6 p-5 bg-white/60 border-l-4 border-[#C9A961] text-left">
                      <div className="text-[#8B4513] text-[10px] tracking-[0.3em] uppercase font-serif-display mb-3">
                        Réponse automatique
                      </div>
                      <p className="text-[#2B1810]/85 text-sm font-serif-display italic leading-relaxed whitespace-pre-line">
                        {autoResponse}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setAutoResponse(null);
                    }}
                    className="mt-8 text-[#8B4513] hover:text-[#5C2E03] text-xs tracking-[0.25em] uppercase font-serif-display underline underline-offset-4 transition-colors"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-3xl text-[#2B1810] mb-2">
                    Demande de rendez-vous
                  </h3>
                  <p className="text-[#5C2E03]/75 text-sm font-serif-display italic mb-8">
                    Réponse personnalisée sous 24h
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot — hidden from humans, attractive to bots */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Site web (laisser vide)</label>
                      <input
                        id="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                      />
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-[#2B1810] text-xs tracking-[0.2em] uppercase font-serif-display mb-2">
                        Nom complet *
                      </label>
                      <input
                        id="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-3 bg-white/70 border border-[#8B4513]/25 focus:border-[#8B4513] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-[#2B1810] placeholder:text-[#5C2E03]/40 font-serif-display transition-all"
                        placeholder="Votre nom"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-[#2B1810] text-xs tracking-[0.2em] uppercase font-serif-display mb-2">
                          Email *
                        </label>
                        <input
                          id="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full px-4 py-3 bg-white/70 border border-[#8B4513]/25 focus:border-[#8B4513] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-[#2B1810] placeholder:text-[#5C2E03]/40 font-serif-display transition-all"
                          placeholder="vous@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-[#2B1810] text-xs tracking-[0.2em] uppercase font-serif-display mb-2">
                          Téléphone
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full px-4 py-3 bg-white/70 border border-[#8B4513]/25 focus:border-[#8B4513] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-[#2B1810] placeholder:text-[#5C2E03]/40 font-serif-display transition-all"
                          placeholder="+33 6 ..."
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-[#2B1810] text-xs tracking-[0.2em] uppercase font-serif-display mb-2">
                        Votre demande *
                      </label>
                      <textarea
                        id="message"
                        required
                        rows={5}
                        minLength={10}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full px-4 py-3 bg-white/70 border border-[#8B4513]/25 focus:border-[#8B4513] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-[#2B1810] placeholder:text-[#5C2E03]/40 font-serif-display transition-all resize-none"
                        placeholder="Décrivez votre projet : pièce souhaitée, type de cuir, délai, occasion..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="group w-full flex items-center justify-center gap-3 px-8 py-4 bg-[#2B1810] hover:bg-[#5C2E03] text-[#F8F1E4] font-medium text-sm tracking-[0.25em] uppercase transition-all duration-500 disabled:opacity-60 disabled:cursor-not-allowed rounded-sm"
                    >
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 border-2 border-[#C9A961] border-t-transparent rounded-full animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer ma demande
                          <Send className="group-hover:translate-x-1 transition-transform duration-300" size={16} />
                        </>
                      )}
                    </button>
                    <p className="text-[#5C2E03]/60 text-xs font-serif-display italic text-center mt-2">
                      Vos données restent confidentielles et ne sont jamais partagées.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
